/**
 * Mobile fullscreen shell, host side.
 *
 * In the mobile shell (see `mobile-shell.ts`) the open chat has to read as one app
 * filling the phone, not a card floating over the website. The iframe cannot
 * do that on its own: it tracks `window.visualViewport` so the on-screen
 * keyboard can never push the composer out of sight, which means it stops
 * short of everything the visual viewport excludes -- the strip behind
 * Safari's URL bar, the home-indicator safe area, the gap that opens while the
 * keyboard animates.
 *
 * So the two jobs are split. The iframe owns the interactive surface and stays
 * on the visual viewport; this module paints everything around it -- host
 * overlay, `html`, `body` and the browser chrome via `theme-color` -- in the
 * same colour, so no seam and no white page ever shows through.
 */

import { isMobileShell } from './mobile-shell';

const CHAT_OPEN_CLASS = 'dhsv100-chat-open';
const BACKDROP_VAR = '--dhsv100-chat-backdrop';
const OWNED_THEME_COLOR_ATTR = 'data-dhsv100-chat-theme-color';

// Below this the shrunken visual viewport is a URL-bar collapse or a rounding
// wobble, not a keyboard.
const KEYBOARD_MIN_OVERLAP_PX = 80;

export interface SafeAreaInsets {
	top: number;
	right: number;
	bottom: number;
	left: number;
}

const ZERO_INSETS: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

// Inline styles the scroll lock overwrites, so the exact original values --
// including "was never set" -- can be put back on close.
const BODY_LOCK_PROPS = [
	'position',
	'top',
	'left',
	'right',
	'width',
	'overflow',
] as const;

type ThemeColorSnapshot = {
	el: HTMLMetaElement;
	parent: Node;
	nextSibling: Node | null;
};

let safeAreaProbe: HTMLDivElement | null = null;
let savedBodyInline: Record< string, string > | null = null;
let lockedScrollY = 0;
let themeColorSnapshot: ThemeColorSnapshot[] | null = null;

// ---------------------------------------------------------------------------
// Viewport meta
// ---------------------------------------------------------------------------

/**
 * Add `viewport-fit=cover` to the host page's viewport meta.
 *
 * Without it the layout viewport stops at the safe areas and `env(safe-area-
 * inset-*)` reports zero, so the home-indicator strip keeps the website's
 * colour no matter what the overlay is painted with.
 *
 * Applied once at init rather than per open: Safari honours the descriptor
 * reliably at first layout, and mutating it later is not dependable.
 */
export function ensureViewportFitCover(): void {
	const existing = document.querySelector< HTMLMetaElement >(
		'meta[name="viewport"]'
	);

	if ( ! existing ) {
		const meta = document.createElement( 'meta' );
		meta.setAttribute( 'name', 'viewport' );
		meta.setAttribute(
			'content',
			'width=device-width, initial-scale=1, viewport-fit=cover'
		);
		document.head.appendChild( meta );
		return;
	}

	const content = existing.getAttribute( 'content' ) || '';
	if ( /(^|[,;\s])viewport-fit\s*=/.test( content ) ) {
		return;
	}

	const normalised = content.trim().replace( /[,;]\s*$/, '' );
	existing.setAttribute(
		'content',
		normalised
			? `${ normalised }, viewport-fit=cover`
			: 'viewport-fit=cover'
	);
}

// ---------------------------------------------------------------------------
// Safe areas
// ---------------------------------------------------------------------------

/**
 * Measure the safe-area insets of the host document.
 *
 * `env(safe-area-inset-*)` resolves to zero inside an iframe -- only the
 * top-level document is told about the notch and the home indicator -- so the
 * iframe app cannot read these itself and is sent the values instead.
 */
export function readSafeAreaInsets(): SafeAreaInsets {
	if ( ! document.body ) {
		return ZERO_INSETS;
	}

	if ( ! safeAreaProbe || ! safeAreaProbe.isConnected ) {
		safeAreaProbe = document.createElement( 'div' );
		safeAreaProbe.setAttribute( 'aria-hidden', 'true' );
		safeAreaProbe.style.cssText = [
			'position:fixed',
			'top:0',
			'left:0',
			'width:0',
			'height:0',
			'visibility:hidden',
			'pointer-events:none',
			'padding-top:env(safe-area-inset-top,0px)',
			'padding-right:env(safe-area-inset-right,0px)',
			'padding-bottom:env(safe-area-inset-bottom,0px)',
			'padding-left:env(safe-area-inset-left,0px)',
		].join( ';' );
		document.body.appendChild( safeAreaProbe );
	}

	const style = window.getComputedStyle( safeAreaProbe );
	const read = ( value: string ): number => {
		const parsed = parseFloat( value );
		return Number.isFinite( parsed ) ? Math.max( 0, parsed ) : 0;
	};

	return {
		top: read( style.paddingTop ),
		right: read( style.paddingRight ),
		bottom: read( style.paddingBottom ),
		left: read( style.paddingLeft ),
	};
}

/**
 * How much of the layout viewport the on-screen keyboard is covering.
 */
export function getKeyboardOverlapPx(): number {
	const viewport = window.visualViewport;
	if ( ! viewport ) {
		return 0;
	}
	const layoutHeight = window.innerHeight;
	const covered = layoutHeight - ( viewport.height + viewport.offsetTop );
	return covered > 0 ? covered : 0;
}

/**
 * How far the compact panel has to lift to stay above the on-screen keyboard.
 *
 * Only coarse-pointer devices outside the mobile shell (tablets) get a value:
 * there the iframe stays at the layout viewport size, so its own media queries
 * do not flip when the keyboard shrinks the visual viewport, and the overlap is
 * reported instead. Phones are handled by resizing the iframe to the visual
 * viewport; a mouse device has no on-screen keyboard.
 */
export function getKeyboardInsetPx(): number {
	if ( isMobileShell() || typeof window.matchMedia !== 'function' ) {
		return 0;
	}
	if ( ! window.matchMedia( '(pointer: coarse)' ).matches ) {
		return 0;
	}
	const overlap = getKeyboardOverlapPx();
	return overlap > KEYBOARD_MIN_OVERLAP_PX ? Math.round( overlap ) : 0;
}

/**
 * The insets the iframe content actually has to pad for.
 *
 * The keyboard sits on top of the home indicator, so once it is up the bottom
 * inset is already accounted for by the shrunken visual viewport -- padding
 * for it again would float the composer above the keyboard.
 */
export function getEffectiveSafeAreaInsets(): SafeAreaInsets {
	const insets = readSafeAreaInsets();
	const overlap = getKeyboardOverlapPx();
	if ( overlap <= KEYBOARD_MIN_OVERLAP_PX ) {
		return insets;
	}
	return { ...insets, bottom: Math.max( 0, insets.bottom - overlap ) };
}

// ---------------------------------------------------------------------------
// Scroll lock
// ---------------------------------------------------------------------------

/**
 * Freeze the host page behind the chat and remember where it was.
 *
 * `position: fixed` rather than `overflow: hidden`: iOS Safari keeps scrolling
 * the document behind an `overflow: hidden` body, and a page that drifts under
 * the chat is exactly the seam this whole module exists to remove. It also
 * pins the layout viewport, so focusing the composer cannot make Safari scroll
 * the page under the keyboard -- which is what produces the open/close jumps.
 */
function lockScroll(): void {
	if ( savedBodyInline ) {
		return;
	}

	const body = document.body;
	lockedScrollY = window.scrollY || window.pageYOffset || 0;

	savedBodyInline = {};
	BODY_LOCK_PROPS.forEach( ( prop ) => {
		savedBodyInline![ prop ] = body.style.getPropertyValue( prop );
	} );

	body.style.setProperty( 'position', 'fixed' );
	body.style.setProperty( 'top', `-${ lockedScrollY }px` );
	body.style.setProperty( 'left', '0' );
	body.style.setProperty( 'right', '0' );
	body.style.setProperty( 'width', '100%' );
	body.style.setProperty( 'overflow', 'hidden' );
}

function unlockScroll(): void {
	if ( ! savedBodyInline ) {
		return;
	}

	const body = document.body;
	BODY_LOCK_PROPS.forEach( ( prop ) => {
		const previous = savedBodyInline![ prop ];
		if ( previous ) {
			body.style.setProperty( prop, previous );
		} else {
			body.style.removeProperty( prop );
		}
	} );
	savedBodyInline = null;

	// Leave the DOM as it was found: a `style=""` left behind on the body is
	// harmless but shows up in every diff a theme developer looks at.
	if ( body.getAttribute( 'style' ) === '' ) {
		body.removeAttribute( 'style' );
	}

	// Taking the body out of flow put the document back at the top, so the
	// scroll position has to be restored by hand -- instantly, or a theme's
	// `scroll-behavior: smooth` animates the page back and reads as a jump.
	const root = document.documentElement;
	const previousBehavior = root.style.scrollBehavior;
	root.style.scrollBehavior = 'auto';
	window.scrollTo( 0, lockedScrollY );
	root.style.scrollBehavior = previousBehavior;
	lockedScrollY = 0;
}

// ---------------------------------------------------------------------------
// theme-color
// ---------------------------------------------------------------------------

/**
 * Paint the browser chrome (Safari's URL bar and its bottom controls) in the
 * chat colour.
 *
 * The existing tags are detached and one owned tag is appended rather than
 * rewriting `content` in place: Safari does not reliably re-read a mutated
 * `theme-color`, and a site with `media`-scoped light/dark variants would
 * otherwise keep whichever one matches.
 *
 * @param {string} color The colour to paint the browser chrome with.
 */
function applyThemeColor( color: string ): void {
	if ( themeColorSnapshot ) {
		return;
	}

	const metas = Array.from(
		document.querySelectorAll< HTMLMetaElement >(
			'meta[name="theme-color"]'
		)
	);

	themeColorSnapshot = metas
		.filter( ( el ) => el.parentNode )
		.map( ( el ) => ( {
			el,
			parent: el.parentNode as Node,
			nextSibling: el.nextSibling,
		} ) );

	themeColorSnapshot.forEach( ( { el } ) => el.remove() );

	const owned = document.createElement( 'meta' );
	owned.setAttribute( 'name', 'theme-color' );
	owned.setAttribute( 'content', color );
	owned.setAttribute( OWNED_THEME_COLOR_ATTR, '' );
	document.head.appendChild( owned );
}

function restoreThemeColor(): void {
	if ( ! themeColorSnapshot ) {
		return;
	}

	document
		.querySelectorAll( `meta[${ OWNED_THEME_COLOR_ATTR }]` )
		.forEach( ( el ) => el.remove() );

	themeColorSnapshot.forEach( ( { el, parent, nextSibling } ) => {
		parent.insertBefore( el, nextSibling );
	} );
	themeColorSnapshot = null;
}

// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------

/**
 * Whether the chat opens fullscreen on this viewport (phone layout).
 */
export function isMobileFullscreenViewport(): boolean {
	return isMobileShell();
}

/**
 * Put the host page into the fullscreen chat state.
 *
 * The class goes on unconditionally so themes and CSS can key off it at any
 * width; the scroll lock and the chrome recolouring are phone-only, because
 * desktop behaviour must not change.
 *
 * @param {string} backgroundColor The chat surface colour to match.
 */
export function enterMobileFullscreen( backgroundColor: string ): void {
	const root = document.documentElement;
	root.style.setProperty( BACKDROP_VAR, backgroundColor );
	root.classList.add( CHAT_OPEN_CLASS );

	if ( ! isMobileFullscreenViewport() ) {
		return;
	}

	lockScroll();
	applyThemeColor( backgroundColor );
}

/**
 * Undo everything `enterMobileFullscreen()` did, including the scroll position.
 */
export function exitMobileFullscreen(): void {
	const root = document.documentElement;

	// Unconditional, not gated on the current width: a phone rotated into
	// tablet width while the chat was open would otherwise stay locked.
	restoreThemeColor();
	unlockScroll();

	root.classList.remove( CHAT_OPEN_CLASS );
	root.style.removeProperty( BACKDROP_VAR );
}

/**
 * Re-evaluate the phone-only parts after a viewport change.
 *
 * Crossing the breakpoint mid-session (rotation, a resized tablet window) has
 * to add or drop the scroll lock and the chrome colour, or the chat is left in
 * the wrong half of the two behaviours.
 *
 * @param {string} backgroundColor The chat surface colour to match.
 */
export function syncMobileFullscreen( backgroundColor: string ): void {
	if ( ! document.documentElement.classList.contains( CHAT_OPEN_CLASS ) ) {
		return;
	}

	if ( isMobileFullscreenViewport() ) {
		lockScroll();
		applyThemeColor( backgroundColor );
		return;
	}

	restoreThemeColor();
	unlockScroll();
}
